import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import styled from '@emotion/styled';

const QuestionCard = styled(Card)({
  padding: '24px',
  marginBottom: '24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
});

const StyledButton = styled(Button)({
  backgroundColor: '#007C3F',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#006F3C',
  },
});

const steps = ['Server Type', 'Requirements', 'Configuration', 'Summary'];

const ServerQuestions = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({
    serverType: '',
    cpuCores: '',
    ram: '',
    storage: '',
    operatingSystem: '',
    managementLevel: '',
  });

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleChange = (event) => {
    setAnswers({
      ...answers,
      [event.target.name]: event.target.value,
    });
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <QuestionCard>
            <FormControl component="fieldset">
              <FormLabel component="legend">What type of server do you need?</FormLabel>
              <RadioGroup
                name="serverType"
                value={answers.serverType}
                onChange={handleChange}
              >
                <FormControlLabel 
                  value="vps" 
                  control={<Radio />} 
                  label="Virtual Private Server (VPS)" 
                />
                <FormControlLabel 
                  value="dedicated" 
                  control={<Radio />} 
                  label="Dedicated Server" 
                />
                <FormControlLabel 
                  value="cloud" 
                  control={<Radio />} 
                  label="Cloud Server" 
                />
              </RadioGroup>
            </FormControl>
          </QuestionCard>
        );
      case 1:
        return (
          <QuestionCard>
            <Typography variant="h6" gutterBottom>
              Server Requirements
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl>
                <FormLabel>Number of CPU Cores</FormLabel>
                <RadioGroup
                  name="cpuCores"
                  value={answers.cpuCores}
                  onChange={handleChange}
                >
                  <FormControlLabel value="2" control={<Radio />} label="2 Cores" />
                  <FormControlLabel value="4" control={<Radio />} label="4 Cores" />
                  <FormControlLabel value="8" control={<Radio />} label="8 Cores" />
                  <FormControlLabel value="16" control={<Radio />} label="16 Cores" />
                </RadioGroup>
              </FormControl>
              
              <FormControl>
                <FormLabel>RAM Size</FormLabel>
                <RadioGroup
                  name="ram"
                  value={answers.ram}
                  onChange={handleChange}
                >
                  <FormControlLabel value="4" control={<Radio />} label="4 GB" />
                  <FormControlLabel value="8" control={<Radio />} label="8 GB" />
                  <FormControlLabel value="16" control={<Radio />} label="16 GB" />
                  <FormControlLabel value="32" control={<Radio />} label="32 GB" />
                </RadioGroup>
              </FormControl>
            </Box>
          </QuestionCard>
        );
      case 2:
        return (
          <QuestionCard>
            <Typography variant="h6" gutterBottom>
              Server Configuration
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl>
                <FormLabel>Operating System</FormLabel>
                <RadioGroup
                  name="operatingSystem"
                  value={answers.operatingSystem}
                  onChange={handleChange}
                >
                  <FormControlLabel value="ubuntu" control={<Radio />} label="Ubuntu" />
                  <FormControlLabel value="centos" control={<Radio />} label="CentOS" />
                  <FormControlLabel value="windows" control={<Radio />} label="Windows Server" />
                </RadioGroup>
              </FormControl>
              
              <FormControl>
                <FormLabel>Management Level</FormLabel>
                <RadioGroup
                  name="managementLevel"
                  value={answers.managementLevel}
                  onChange={handleChange}
                >
                  <FormControlLabel value="unmanaged" control={<Radio />} label="Unmanaged" />
                  <FormControlLabel value="managed" control={<Radio />} label="Managed" />
                  <FormControlLabel value="fully_managed" control={<Radio />} label="Fully Managed" />
                </RadioGroup>
              </FormControl>
            </Box>
          </QuestionCard>
        );
      case 3:
        return (
          <QuestionCard>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>Server Type: {answers.serverType}</Typography>
              <Typography>CPU Cores: {answers.cpuCores}</Typography>
              <Typography>RAM: {answers.ram} GB</Typography>
              <Typography>Operating System: {answers.operatingSystem}</Typography>
              <Typography>Management Level: {answers.managementLevel}</Typography>
            </Box>
          </QuestionCard>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
        Server Configuration Wizard
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {getStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>
        <StyledButton
          variant="contained"
          onClick={activeStep === steps.length - 1 ? undefined : handleNext}
        >
          {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
        </StyledButton>
      </Box>
    </Container>
  );
};

export default ServerQuestions; 